import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './TruckScales.css';
import { useProducts, type TruckScalesSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const TruckScales: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products: indicators, loading, error } = useProducts<TruckScalesSpecs>('truck-scales', editMode);
  const navigate = useNavigate();

  const additionalOptions = [
    {
      title: 'Impresoras Epson',
      description: 'Para comprobantes y reportes de pesaje',
    },
    {
      title: 'Software PC',
      description: 'Software de pesaje para gestión avanzada',
    },
    {
      title: 'Regulador de Voltaje',
      description: 'Protección contra variaciones eléctricas',
    },
  ];

  const applications = [
    {
      title: 'Industria Agroindustrial',
      description: 'Pesaje de camiones en granjas, silos y plantas de procesamiento de granos.',
    },
    {
      title: 'Minería y Canteras',
      description: 'Control de cargas en volquetes y camiones de extracción de materiales.',
    },
    {
      title: 'Logística y Transporte',
      description: 'Verificación de peso en centros de distribución y terminales de carga.',
    },
    {
      title: 'Industria Química',
      description: 'Medición precisa en el traslado de materias primas y productos terminados.',
    },
    {
      title: 'Construcción',
      description: 'Pesaje de mezcladoras, pipas y camiones con materiales de construcción.',
    },
    {
      title: 'Industria Papelera',
      description: 'Control de tonelaje en el transporte de pulpa, papel y materiales reciclados.',
    },
  ];

  return (
    <div className="ts-page">
      {error && <div className="ts-status-bar ts-status-bar--error"><p>Error al cargar productos. Por favor intente de nuevo.</p></div>}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="ts-hero">
        <div className="container ts-hero-content">
          <nav className="ts-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="ts-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="ts-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Básculas Camioneras</span>
          </nav>
          <h1 className="ts-hero-title">Básculas Camioneras</h1>
          <p className="ts-hero-subtitle">
            Sistemas de pesaje de alta capacidad construidos en acero estructural
            ASTM‑SA‑36 para vehículos pesados y aplicaciones industriales de alto volumen.
          </p>
          {authType === 'internal' && (
            <button
              className={`ts-btn-edit-mode${editMode ? ' ts-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="ts-hero-image" aria-hidden="true">
          <img src="/images/truck-scales.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="ts-overview">
        <div className="container">
          <div className="ts-overview-grid">
            <div className="ts-overview-text">
              <span className="ts-label">Descripción general</span>
              <h2>Precisión y durabilidad en cada pesaje</h2>
              <p>
                Nuestras básculas camioneras están diseñadas para proporcionar mediciones
                precisas y confiables en aplicaciones industriales de alto volumen.
                Construidas con acero estructural de alta calidad y equipadas con celdas
                de carga Weigh Bar de Avery Weigh-Tronix, reconocidas mundialmente por
                su precisión y durabilidad. Cada sistema incluye software de pesaje y
                cuenta con opciones de indicadores avanzados para adaptarse a los
                requerimientos específicos de su operación.
              </p>
             
            </div>
            <div className="ts-overview-image">
               <ul className="ts-feature-list" aria-label="Características principales">
                {[
                  'Construcción robusta en acero estructural ASTM-SA-36',
                  'Celdas de carga Weigh Bar de Avery Weigh-Tronix',
                  'Software de pesaje incluido',
                  'Opciones de indicadores avanzados',
                  'Instalación profesional y capacitación',
                ].map((item, i) => (
                  <li key={i} className="ts-feature-item">
                    <svg className="ts-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Products ─────────────────────────────────────────── */}
      <section className="ts-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Tipos de básculas camioneras</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/truck-scales')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : indicators.map((indicator, index) => (
                <ProductCard
                  key={index}
                  reviewed={indicator.reviewed}
                  endOfLife={indicator.endOfLife}
                  productId={indicator.productId}
                  name={indicator.name}
                  badge={indicator.specs?.model ?? undefined}
                  description={indicator.specs?.description ?? undefined}
                  image={indicator.image}
                  brand="AWTX"
                  onRevisar={editMode && indicator.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(indicator.specs?.features?.length          ? [{ icon: '⚙️', label: 'Características', items: (indicator.specs?.features  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(indicator.specs?.included?.length          ? [{ icon: '📋', label: 'Incluye',          items: (indicator.specs?.included  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(indicator.specs?.optionalIndicators?.length ? [{ icon: '🖥️', label: 'Indicadores',     items: (indicator.specs?.optionalIndicators  ?? []).filter((x): x is string => x != null)}] : []),
                    ...(indicator.specs?.optionalAddons?.length    ? [{ icon: '➕', label: 'Opcionales',       items: (indicator.specs?.optionalAddons  ?? []).filter((x): x is string => x != null)}] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Applications ─────────────────────────────────────── */}
      <section className="ts-applications">
        <div className="container">
          <span className="ts-label ts-label--light">Industrias</span>
          <h2 className="ts-section-title ts-section-title--light">Aplicaciones</h2>
          <div className="ts-applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="ts-app-card">
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Additional Options ───────────────────────────────── */}
      <section className="ts-options">
        <div className="container">
          <div className="ts-options-inner">
            <div className="ts-options-header">
              <span className="ts-label">Personalización</span>
              <h2 className="ts-section-title">Opciones adicionales</h2>
              <p>
                Complemente su báscula camionera con accesorios y servicios
                diseñados para optimizar su operación de pesaje.
              </p>
            </div>
            <ul className="ts-options-list" aria-label="Opciones adicionales">
              {additionalOptions.map((option, index) => (
                <li key={index} className="ts-option-item">
                  <svg className="ts-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <div>
                    <strong>{option.title}</strong>
                    <span className="ts-option-desc"> — {option.description}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── Brands ───────────────────────────────────────────── */}
      <section className="ts-brands">
        <div className="container">
          <span className="ts-label">Marcas y tecnología</span>
          <h2 className="ts-section-title">Respaldo de marca</h2>
          <div className="ts-brands-grid">
            <div className="ts-brand-card">
              <h3>Avery Weigh-Tronix</h3>
              <p>
                Utilizamos celdas de carga Weigh Bar de Avery Weigh-Tronix,
                reconocidas mundialmente por su precisión y durabilidad en
                aplicaciones industriales de alto rendimiento.
              </p>
            </div>
            <div className="ts-brand-card">
              <h3>Epson</h3>
              <p>
                Impresoras Epson disponibles para documentación completa
                de transacciones de pesaje y generación de reportes.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TruckScales;
